import React from 'react';

import { Route, Redirect } from 'react-router-dom';

import { usePermissionContext } from '../core/PermissionContext';

interface PermissionRouteProps {
  authRule: string | string[];
  canMethod?: 'oneof' | 'allof';
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  component?: React.ComponentType<any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  render?: (props: any) => React.ReactNode;
  unauthorizedComponent?: React.ComponentType;
  loadingComponent?: React.ComponentType;
  path?: string;
  exact?: boolean;
}

function PermissionRoute({
  authRule,
  canMethod = 'oneof',
  component: Component = undefined,
  render: Render = undefined,
  unauthorizedComponent = undefined,
  loadingComponent: LoadingComponent = undefined,
  path = undefined,
  exact = undefined,
  ...rest
}: PermissionRouteProps): React.ReactElement {
  const { engine, isLoading, error } = usePermissionContext();
  const rules = Array.isArray(authRule) ? authRule : [authRule];

  if (!engine.isReady()) {
    return (
      <Route
        {...rest}
        exact={exact}
        path={path}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        render={(props: any) => {
          if (isLoading) {
            if (LoadingComponent) {
              return <LoadingComponent {...props} />;
            }
            return null;
          }
          return error?.message ?? 'Failed to load permissions';
        }}
      />
    );
  }

  let authorized = false;
  if (canMethod === 'allof') {
    authorized = rules.every((rule) => engine.hasPageAccess(rule));
  } else {
    authorized = rules.some((rule) => engine.hasPageAccess(rule));
  }

  if (!authorized) {
    if (unauthorizedComponent) {
      return (
        <Route
          {...rest}
          component={unauthorizedComponent}
          exact={exact}
          path={path}
        />
      );
    }
    return (
      <Route
        {...rest}
        exact={exact}
        path={path}
        render={() => <Redirect to="/unauthorized" />}
      />
    );
  }

  const routeProps = Render ? { render: Render } : { component: Component };

  return (
    <Route
      {...rest}
      {...routeProps}
      exact={exact}
      path={path}
    />
  );
}

export { PermissionRoute };
